/**
 * MODULE: core/auth/frontend/components/AuthProvider.tsx
 * PURPOSE: Auth context and provider; persists user, tokens, and tenant_id (localStorage) from /me and login.
 * DEPENDENCIES: react, @shared/utils, @shared/lib/frontend/api/authTokenRefresh (**refreshBraznAccessTokenOnce**), authApi, auth.types
 * PUBLIC API: AuthContext, AuthProvider, getTenantIdFromUser (tenant_id or tenant.id)
 * SIDE EFFECTS: localStorage (access_token, refresh_token, tenant_id, return_url); optional LinkedIn extension sync (chrome.runtime.sendMessage) when VITE_BRAZN_LINKEDIN_EXTENSION_ID is set
 * AGENT SAFETY: PROTECTED
 * Recent: 2026-08-03 [2.2.2024] — Remove Session Expiring Soon modal; silent token refresh before JWT expiry.
 * Recent_PRIOR: 2026-06-04 [2.2.1273] — Session extend uses **`refreshBraznAccessTokenOnce({ force: true })`**; warning scheduler no immediate re-open loop; **`clearAuth`** stable callback.
 * Recent_PRIOR: 2026-05-04 — **tryRefreshToken** uses **`refreshBraznAccessTokenOnce`** (same gateway path as **zoomApi** JWT retry) then **getCurrentUser** for tenant or user sync.
 */
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  ReactNode,
} from 'react';
import { clearBraznLinkedInExtensionAuth, syncBraznLinkedInExtensionAuth } from '@shared/utils';
import { refreshBraznAccessTokenOnce } from '@shared/lib/frontend/api/authTokenRefresh';
import { authApi } from '../api/authApi';
import type {
  AuthContextValue,
  AuthState,
  LoginRequest,
} from '../../contracts/auth.types';

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TENANT_ID_KEY = 'tenant_id';
const RETURN_URL_KEY = 'auth_return_url';
/** Refresh access token this many minutes before JWT exp (no user-facing modal). */
const SESSION_REFRESH_BEFORE_EXPIRY_MINUTES = 5;

/** Get tenant ID from user (backend may return tenant_id or tenant.id). */
function getTenantIdFromUser(user: { tenant_id?: string; tenant?: { id?: string } } | null): string | undefined {
  return user?.tenant_id ?? user?.tenant?.id;
}

/** Push access_token + tenant_id to LinkedIn extension (no-op if extension ID unset). */
function pushSessionToLinkedInExtension(): void {
  const token = localStorage.getItem(TOKEN_KEY);
  const tenantId = localStorage.getItem(TENANT_ID_KEY);
  if (token && tenantId) {
    syncBraznLinkedInExtensionAuth(token, tenantId);
  }
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

function parseJwtExpiry(token: string): number | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(initialState);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tryRefreshTokenRef = useRef<(options?: { force?: boolean }) => Promise<boolean>>(
    async () => false,
  );
  const scheduleSilentRefreshRef = useRef<(token: string) => void>(() => undefined);

  const clearAuth = useCallback(() => {
    clearBraznLinkedInExtensionAuth();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TENANT_ID_KEY);
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const redirectOnExpired = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    clearAuth();
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== '/auth' && currentPath !== '/login') {
      localStorage.setItem(RETURN_URL_KEY, currentPath);
    }
    window.location.href = '/auth?expired=true';
  }, [clearAuth]);

  const scheduleSilentRefresh = useCallback(
    (token: string) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      const expiry = parseJwtExpiry(token);
      if (!expiry) return;

      const refreshAt = expiry - SESSION_REFRESH_BEFORE_EXPIRY_MINUTES * 60 * 1000;
      const delayMs = refreshAt - Date.now();

      const runSilentRefresh = () => {
        void (async () => {
          const success = await tryRefreshTokenRef.current({ force: true });
          if (!success) {
            redirectOnExpired();
            return;
          }
          const next = localStorage.getItem(TOKEN_KEY);
          if (next) {
            scheduleSilentRefreshRef.current(next);
            pushSessionToLinkedInExtension();
          }
        })();
      };

      if (delayMs <= 0) {
        runSilentRefresh();
        return;
      }

      refreshTimerRef.current = setTimeout(runSilentRefresh, delayMs);
    },
    [redirectOnExpired],
  );

  scheduleSilentRefreshRef.current = scheduleSilentRefresh;

  // Initialize auth state from stored token
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);

      if (!token) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const user = await authApi.getCurrentUser();
        const tenantId = getTenantIdFromUser(user as { tenant_id?: string; tenant?: { id?: string } });
        if (tenantId) {
          localStorage.setItem(TENANT_ID_KEY, tenantId);
        }
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        scheduleSilentRefresh(token);
        pushSessionToLinkedInExtension();
      } catch {
        // Token invalid, try refresh
        const ok = await tryRefreshTokenRef.current();
        if (ok) {
          const next = localStorage.getItem(TOKEN_KEY);
          if (next) scheduleSilentRefresh(next);
        }
      }
    };

    void initAuth();

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [scheduleSilentRefresh]);

  const tryRefreshToken = async (options?: { force?: boolean }): Promise<boolean> => {
    const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!refreshTokenValue?.trim()) {
      clearAuth();
      return false;
    }

    try {
      const refreshed = await refreshBraznAccessTokenOnce(options);
      if (!refreshed) {
        clearAuth();
        return false;
      }

      const user = await authApi.getCurrentUser();
      const tenantId = getTenantIdFromUser(user as { tenant_id?: string; tenant?: { id?: string } });
      if (tenantId) {
        localStorage.setItem(TENANT_ID_KEY, tenantId);
      }
      setState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      pushSessionToLinkedInExtension();
      return true;
    } catch {
      clearAuth();
      return false;
    }
  };

  tryRefreshTokenRef.current = tryRefreshToken;

  const login = useCallback(
    async (credentials: LoginRequest) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await authApi.login(credentials);

        localStorage.setItem(TOKEN_KEY, response.access_token);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token);
        const tenantId = getTenantIdFromUser(
          response.user as { tenant_id?: string; tenant?: { id?: string } },
        );
        if (tenantId) {
          localStorage.setItem(TENANT_ID_KEY, tenantId);
        }

        setState({
          user: response.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });

        scheduleSilentRefresh(response.access_token);
        pushSessionToLinkedInExtension();

        const returnUrl = localStorage.getItem(RETURN_URL_KEY);
        if (returnUrl) {
          localStorage.removeItem(RETURN_URL_KEY);
          window.location.href = returnUrl;
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Login failed',
        }));
        throw error;
      }
    },
    [scheduleSilentRefresh],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  const refreshToken = useCallback(async () => {
    const success = await tryRefreshToken();
    if (!success) {
      throw new Error('Token refresh failed');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      refreshToken,
    }),
    [state, login, logout, refreshToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
